import React, { useState, useEffect } from 'react';
import Styled from 'styled';
import classnames from 'classnames';
import { FormattedMessage } from 'react-intl';
import { useMutation } from '@apollo/client/react/hooks';

import TextArea from 'four-nets/ui-v2/form/text-area/text-area';
import { emptyValidator } from 'four-nets/ui-v2/form/validators';
import { UserBrandFragment } from 'four-nets/user-brand/user-brands/user-brand.graphql';
import { VendorFragment } from 'four-nets/vendor/vendor/vendor.graphql';
import { Style as SelectStyle } from 'four-nets/ui-v2/form/select/select';

import BrandInput, { Brand } from '../brand-input/brand-input';
import ServicesSelect from '../services-select/services-select';

import mutation, {
    CreateUserBrandMutation,
    CreateUserBrandMutationVariables,
} from './mutations/create-user-brand.graphql';
import updateMutation, {
    UpdateUserBrandMutation,
    UpdateUserBrandMutationVariables,
} from './mutations/update-user-brand.graphql';
import defaultStyles from './create-user-brand.scss';

export interface Props {
    styles: typeof defaultStyles;
    refetch: () => void;
    userBrand?: UserBrandFragment;
    vendor?: VendorFragment;
    open: boolean;
    close: () => void;
}

interface Field<T> {
    value: T | null;
    error?: React.ReactNode | null;
}

const CreateUserBrand: React.FC<Props> = (props, context) => {
    const { styles, refetch, userBrand, vendor, open, close } = props;
    const defaultBrand: Brand | null = vendor ? vendor.brand : null;

    const [createFn] = useMutation<CreateUserBrandMutation, CreateUserBrandMutationVariables>(mutation);
    const [updateFn] = useMutation<UpdateUserBrandMutation, UpdateUserBrandMutationVariables>(updateMutation);

    const defaults = {
        serviceId: { value: '' },
        brand: { value: defaultBrand },
        description: { value: '' },
    };

    if (userBrand) {
        defaults.serviceId = { value: userBrand.service.id };
        defaults.brand = { value: userBrand.brand };
        defaults.description = { value: userBrand.description };
    }

    const [serviceId, setServiceId] = useState<Field<string>>(defaults.serviceId);
    const [brand, setBrand] = useState<Field<Brand>>(defaults.brand);
    const [description, setDescription] = useState<Field<string>>(defaults.description);
    const [submit, setSubmit] = useState(false);

    const validate = () => {
        setServiceId({ ...serviceId, error: emptyValidator(serviceId.value) });
        setBrand({
            ...brand,
            error: emptyValidator(brand.value && brand.value.name),
        });
        setDescription({ ...description });
        setSubmit(true);
    };

    const reset = () => {
        setServiceId(defaults.serviceId);
        setBrand(defaults.brand);
        setDescription(defaults.description);
        close();
    };

    useEffect(() => {
        if (submit && !brand.error && !serviceId.error && !description.error) {
            const input = {
                serviceId: serviceId.value,
                brand: brand.value ? (brand.value.id ? brand.value.id : brand.value.name) : '',
                description: description.value,
            };

            let promise: Promise<any>;
            if (userBrand) {
                promise = updateFn({ variables: { input: { ...input, id: userBrand.id } } });
            } else {
                promise = createFn({ variables: { input } });
            }

            promise.then(() => {
                refetch();
                reset();
            });
        }

        if (submit) {
            setSubmit(false);
        }
    }, [submit]);

    const servicesSelectStyle: SelectStyle = {
        select: styles.select,
        element: styles.element,
        container: userBrand ? styles.containerEdit : styles.container,
    };

    return open ? (
        <div
            className={classnames(styles.form, {
                [styles.edit]: Boolean(userBrand),
            })}
        >
            <h3 className={styles.heading}>
                {Boolean(userBrand) ? (
                    <FormattedMessage id='EDIT_RECOMMENDATION' />
                ) : (
                    <FormattedMessage id='ADD_RECOMMENDATION_SUBMIT' values={{ gender: 'v' }} />
                )}
            </h3>
            <div className={styles.field}>
                <ServicesSelect
                    selectStyles={servicesSelectStyle}
                    value={serviceId.value || ''}
                    onChange={e => {
                        setServiceId({ value: e.target.value });
                    }}
                />
            </div>
            {serviceId.error ? <div className={styles.error}>{serviceId.error}</div> : null}
            {vendor ? null : (
                <>
                    <div className={styles.field}>
                        <BrandInput
                            style={{ input: userBrand ? styles.brandInputColor : undefined }}
                            brand={brand.value}
                            onChange={(brand: Brand | null) => {
                                setBrand({ value: brand });
                            }}
                        />
                    </div>
                    {brand.error ? <div className={styles.error}>{brand.error}</div> : null}
                </>
            )}
            <div className={styles.field}>
                <FormattedMessage id='ENTER_RECOMMENDATION_DESC' values={{ gender: 'v' }}>
                    {msg => (
                        <TextArea
                            style={{ textArea: userBrand ? styles.textAreaEdit : styles.textArea }}
                            placeholder={msg as string}
                            value={description.value}
                            onChange={e => {
                                setDescription({ value: e.target.value });
                            }}
                        />
                    )}
                </FormattedMessage>
            </div>
            <div className={styles.buttons}>
                <button onClick={close} className={styles.button}>
                    <FormattedMessage id='CLOSE_WINDOW' values={{ gender: 'v' }} />
                </button>
                <button
                    className={classnames(styles.button, styles.add)}
                    onClick={validate}
                    disabled={!Boolean(serviceId.value || brand.value)}
                >
                    {userBrand ? (
                        <FormattedMessage id='BUTTON_LABEL_SAVE' />
                    ) : (
                        <FormattedMessage id='ADD_RECOMMENDATION_SUBMIT' values={{ gender: 'v' }} />
                    )}
                </button>
            </div>
        </div>
    ) : null;
};

export default Styled(defaultStyles)(CreateUserBrand);
